import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageCircle, Send, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ParentThread() {
  const { token } = useParams();
  const [thread, setThread] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/parent-thread/${token}`);
      setThread(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Lien invalide");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [thread?.messages?.length]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/public/parent-thread/${token}/reply`, { message: reply.trim() });
      setReply("");
      toast.success("Message envoyé");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <Loader2 className="h-6 w-6 animate-spin text-[#002FA7]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8F9FA] p-6">
      <div className="max-w-md text-center">
        <div className="text-7xl font-display font-black tracking-tight text-[#002FA7]">404</div>
        <div className="mt-3 text-sm uppercase tracking-[0.25em] font-bold">{error}</div>
        <Link to="/"><Button className="mt-6 bg-[#002FA7] text-white h-11">Retour à PAG</Button></Link>
      </div>
    </div>
  );

  const messages = thread.messages || [];

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="parent-thread">
      {/* Header */}
      <header className="bg-white border-b border-[#DEE2E6]">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#868E96] flex items-center gap-2">
              <ShieldCheck className="h-3 w-3 text-[#059669]" /> Lien sécurisé · {thread.entreprise_name}
            </div>
            <h1 className="mt-2 font-display text-2xl lg:text-3xl font-black tracking-tight">{thread.title}</h1>
            <div className="mt-1 text-xs text-[#495057]">
              À propos de <span className="font-bold">{thread.eleve_nom}</span>
              {thread.parent_nom && <> · destiné à <span className="font-bold">{thread.parent_nom}</span></>}
            </div>
          </div>
          <Link to="/"><Button variant="outline" className="h-9 text-xs" data-testid="parent-back">PAG</Button></Link>
        </div>
      </header>

      {/* Thread */}
      <div className="max-w-3xl mx-auto p-6 lg:p-10 space-y-3" data-testid="parent-messages">
        {messages.length === 0 ? (
          <div className="text-sm text-[#868E96] text-center py-10">Aucun message.</div>
        ) : messages.map((m, i) => {
          const isParent = m.from === "parent";
          return (
            <div key={i} className={`flex ${isParent ? "justify-end" : "justify-start"}`} data-testid={`msg-${i}`}>
              <div className={`max-w-[80%] p-4 ${isParent ? "bg-[#002FA7] text-white" : "bg-white border border-[#DEE2E6]"}`}>
                <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isParent ? "text-white/70" : "text-[#868E96]"}`}>
                  {m.from_name || (isParent ? "Vous" : "École")} · {new Date(m.created_at).toLocaleString("fr-FR")}
                </div>
                <div className="text-sm whitespace-pre-wrap leading-relaxed">{m.message}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Reply */}
      <div className="sticky bottom-0 bg-white border-t border-[#DEE2E6]">
        <form onSubmit={send} className="max-w-3xl mx-auto p-4 flex gap-2 items-end" data-testid="parent-reply-form">
          <div className="flex-1">
            <Textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={2}
              placeholder="Votre réponse à l'école…"
              data-testid="parent-reply-input"
              onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) send(); }}
            />
            <div className="text-[10px] text-[#868E96] mt-1">⌘+Entrée pour envoyer</div>
          </div>
          <Button type="submit" disabled={sending || !reply.trim()} data-testid="parent-reply-submit" className="h-11 bg-[#002FA7] text-white">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Envoyer</>}
          </Button>
        </form>
      </div>
    </div>
  );
}
